import React, { useEffect, useState, useCallback } from 'react';
import useForm from '@utils/useForm';
import { get, isEmpty, isNil } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { BasicDetailsForm } from '@models/MLTC/BasicDetails.model';
import { Helpers } from '@utils/helpers';
import Accordion from '@components/Accordion';
import ContainerBlock from '@components/ContainerBlock';
import { updateBasicDetails, updateCoverage } from '@redux/slices/MLTC/step1Slice';
import { CoverageForm } from '@models/MLTC/Coverage.model';
import { GAAviva } from '@utils/gaAviva';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import Heading from '@components/Heading';
import InformationBox from '@components/InformationBox';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import MultiSteps from '@components/MultiSteps';
import Text from '@components/Text';
import Button from '@components/Button';
import DayOfBirth from './components/DayOfBirth';
import Bullet from '@components/Bullet';
import Divider from '@components/Divider';
import PlanInformation from './components/PlanInformation';

import { validate } from './service';


const page = 'Basic Details';

const trackOnload = (fromData: BasicDetailsForm): void => {
  const { month, day, year } = fromData.dob;
  const stringDate = `${month}/${day}/${year}`;
  const age = Helpers.getAge(new Date(stringDate));
  const gender = Helpers.getGenderNameByCode(fromData.gender || '');
  const GA = GAAviva.getInstance();
  let { user, form, page } = GA.digitalData;
  user.person.age = `${age}`;
  form.field.Gender = gender;
  form.name = 'Basic Details';
  GA.trackPageLoad({ user, form, page });
}
const trackFormSubmmit = (): void =>  {
  const GA = GAAviva.getInstance();
  GA.trackFormSuccess({});
}

const getGenericError = (): string => {
  const urlParams: URLSearchParams = Helpers.getURLSearchParams();
  console.log(urlParams);
  const dobError = urlParams.get('doberror');
  const genderError = urlParams.get('gendererror');

  if (dobError === 'true') {
    return 'Please ensure date of birth matches with Myinfo data.';
  }
  if (genderError === 'true') {
    return 'Please ensure gender matches with Myinfo data.';
  }
  return '';
}

function BasicDetails() {
  console.log(`QnB ➤ PAGE ➤ Aviva - ${page}`);
  const history = useHistory();
  const dispatch = useAvivaDispatch();
  const brochureUrl = useAvivaSelector(state => state.staticData.brochureUrl);
  const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);
  const basicDetails = useAvivaSelector(state => state.mltcStep1.basicDetails);
  const coverage = useAvivaSelector(state => state.mltcStep1.coverage);
  const paramCC = campaignDetail?.campaignCode ? `?cc=${campaignDetail.campaignCode}` : '';

  const [genericError, setGenericError] = useState<string | undefined>(undefined);

  const onBack = useCallback(() => {
    history.push('/mltc-r2/lets-get-started' + paramCC);
  }, [history]);


  const { handleChange, values, errors, formValid } = useForm(new BasicDetailsForm(basicDetails), () => { }, validate);

  const onChange = (event: any) => {
    handleChange(event);
    const { name, value } = event.target;
    
    console.log(`QnB ➤ onChange(event)`, {
      name,
      value,
      event,
      formValid,
      values,
      coverage
    });

    const nextCoverage = { ...coverage, promoCode: '', promoNric: '' }
    dispatch(updateCoverage(new CoverageForm(nextCoverage)));
  }

  const onContinue = () => {
    dispatch(updateBasicDetails(values));
    trackOnload(values);
    trackFormSubmmit();
    history.push('/mltc-r2/choose-plan' + paramCC);
  }
  
  useEffect(() => {
    setGenericError(getGenericError());
    if (isEmpty(basicDetails)) {
      onBack();
    }
    trackOnload(new BasicDetailsForm(basicDetails));
  }, [basicDetails, onBack]);

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={1} label="Let's get a quick quote..." />
          <Heading margin="0 0 30px"> Let&apos;s get a quick quote... </Heading>
        </FullGrid>

        <LeftGrid>
          {!isEmpty(genericError) && !isNil(genericError) && (
            <InformationBox variant="error">
              <Text>{genericError}</Text>
            </InformationBox>
          )}
          <form onSubmit={e => { e.preventDefault() }} noValidate>
            <InformationBox hasClose>
              <Text>Applicable to Singapore Citizens and Singapore Permanent Residents with valid Singapore NRIC.</Text>
            </InformationBox>

            <Heading variant="label"> Date of birth </Heading>
            {!errors?.dob?.isValid && errors?.dob?.message && (
              <Bullet variant="error">{errors?.dob?.message}</Bullet>
            )}
            <DayOfBirth error={!errors?.dob?.isValid && errors?.dob?.message} value={values['dob']} name="dob" onChange={onChange} />
            <Heading variant="label"> Gender </Heading>
            <RadioButtonGroup value={values['gender']} name="gender" onChange={onChange}>
              <RadioButton text="Male" value='M' />
              <RadioButton text="Female" value='F' />
            </RadioButtonGroup>

            <Heading variant="label"> Are you a smoker? </Heading>
            <RadioButtonGroup value={values['smoker']} name="smoker" onChange={onChange}>
              <RadioButton text="Yes" value='Y' />
              <RadioButton text="No" value='N' />
            </RadioButtonGroup>

            <Divider size={1} margin="0 0 20px" />

            <Accordion title="Important notes" isExpanded>
							<ContainerBlock>
								<Bullet>
									<Text>Singlife CareShield Standard / Singlife CareShield Plus (Supplements) can be purchased by CareShield Life (CSHL) or ElderShield (ESH) policyholders.</Text>
								</Bullet>
								<Bullet>
                All Supplements are regulated under the CareShield Life and Long-Term Care Act.
								</Bullet>
							</ContainerBlock>
						</Accordion>
						<Divider size={4} margin="0 0 20px" />

          </form>

          <Button margin="40px 0 20px" disabled={!formValid} label="Continue" onClick={onContinue}></Button>
          <Button margin="0 0 40px" variant="secondary" label="Back" onClick={onBack} />
        </LeftGrid>

        <EmptyGrid></EmptyGrid>

        <RightGrid>
          <PlanInformation brochureUrl={brochureUrl} />
        </RightGrid>
      </ContainerGrid>
    </Container>
  );
};

export default BasicDetails;
